"use client"
import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { lockScroll, unlockScroll } from '@/shared/ui/scrollLock'

type BottomDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  header?: React.ReactNode
  headerRight?: React.ReactNode
  className?: string
  children: React.ReactNode
  // Z-index menor que o MobileOpportunitySheet (z-40) para nunca ficar acima dos detalhes
  zIndexClass?: string // default: z-[60] (acima do bottom nav z-50; abaixo de sheets > z-80)
  overlayClassName?: string
}

export function BottomDrawer({ open, onOpenChange, title, header, headerRight, className, children, zIndexClass = 'z-[60]', overlayClassName }: BottomDrawerProps) {
  React.useEffect(() => {
    if (!open) return
    lockScroll()
    return () => {
      unlockScroll()
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={cn('fixed inset-0 bg-black/40', zIndexClass, overlayClassName)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
          />
          <motion.aside
            className={cn('fixed inset-x-0 bottom-0 flex w-full max-h-[85vh] flex-col border-t border-border bg-background shadow-xl', zIndexClass, className)}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex-1 min-w-0 text-base font-medium truncate pr-2">
                {header ?? title}
              </div>
              <div className="flex items-center gap-2">
                {headerRight}
                <button aria-label="Fechar" onClick={() => onOpenChange(false)} className="rounded p-1 hover:bg-muted">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 px-4 py-4">
              {children}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
